import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserInfoModel } from 'src/models/usersModel';
import { RootState } from 'src/store/reducers/state';
import { useUserActions } from 'src/store/users/actions';
import ToastMessage from 'src/components/ToastContainer';
import { errorMsg } from '../utilities/utils';

interface MFAScannerModalProps {
  onClose: () => void;
  userData: any;
}

const MFAScannerModal: React.FC<MFAScannerModalProps> = ({ onClose, userData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userActions = useUserActions(dispatch);

  const [step, setStep] = useState<'qr' | 'otp'>('qr');
  const [inputs, setInputs] = useState<string[]>(Array(6).fill(''));
  const [isVerifying, setIsVerifying] = useState(false);

  const { isMfaUpdated, isCodeVerified, qrdata, message } = useSelector((state: RootState) => {
    return {
      isMfaUpdated: state.users.isMfaUpdated,
      isCodeVerified: state.users.isCodeVerified,
      qrdata: state.users.qrdata,
      message: state.users.message
    };
  });

  useEffect(() => {
    userActions.resetMFAStateRequest();
    userActions.generateMFAQrRequest(userData.UserId);
  }, []);

  const handleCancel = () => {
    userActions.resetMFAStateRequest();
    navigate('/account/MultiFactorAuthentication');
    onClose();
  };

  const handleProceedToVerify = () => {
    setStep('otp');
  };

  const handleBackToQr = () => {
    userActions.resetMFAStateRequest();
    setInputs(Array(6).fill(''));
    setIsVerifying(false);
    userActions.generateMFAQrRequest(userData.UserId);
    setStep('qr');
  };

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(0, 1);
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);
    if (value && index < inputs.length - 1) {
      document.getElementById(`setup-otp-input-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !inputs[index] && index > 0) {
      document.getElementById(`setup-otp-input-${index - 1}`)?.focus();
    }
    if (event.key === 'Enter') verifyCode();
  };

  const verifyCode = () => {
    const otp = inputs.join('');
    if (otp.length !== 6) {
      errorMsg('Please enter a 6-digit code');
      return;
    }

    setIsVerifying(true);
    userActions.VerifyMfaCodeRequest({
      userId: userData?.UserId,
      otp
    });
  };

  useEffect(() => {
    if (isCodeVerified && isVerifying) {
      const data = {
        userId: userData?.UserId,
        isMfaSetupEnabled: true,
        userRole: userData?.role
      };
      userActions.UserMfaSetupRequest(data);
    } else if (message === 'Fail' && isVerifying) {
      errorMsg('Invalid code. Please try again.');
      setIsVerifying(false);
      setInputs(Array(6).fill(''));
      setTimeout(() => document.getElementById('setup-otp-input-0')?.focus(), 100);
    }
  }, [isCodeVerified, message, isVerifying]);

  useEffect(() => {
    if (isMfaUpdated && isVerifying) {
      updateMfaSetupStatus(true);
      onClose();
      navigate('/');
    }
  }, [isMfaUpdated, isVerifying]);

  const updateMfaSetupStatus = (isEnabled: boolean) => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      const userData: UserInfoModel = JSON.parse(storedUserData);
      userData.isMfaSetupEnabled = isEnabled;
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  };

  return (
    <>
      <ToastMessage />
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full m-4">
          {step === 'qr' ? (
            <>
              <h1 className="font-black sm:text-2xl text-xl p-5 m-0 border-b">
                Use an authenticator app.
              </h1>
              <h3 className="font-black mb-4 px-4 pt-4">
                Use a free authenticator app (such as Google Authenticator, Microsoft
                Authenticator, or Authy) to scan this QR code to set up your account.
              </h3>
              <div className="m-auto w-auto flex items-center justify-center mb-5">
                <img
                  className="h-60 w-60"
                  src={`${qrdata?.qrCodeImage}`}
                  height="225"
                  width="225"
                  alt="MFA setup QR code"
                />
              </div>
              <div className="p-4 border-t flex items-center justify-end gap-4">
                <button
                  className="btn btn-primary m-0 h-10 font-bold tracking-widest px-3 py-1 w-auto"
                  onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary m-0 h-10 font-bold tracking-widest px-3 py-1 w-auto"
                  onClick={handleProceedToVerify}>
                  Continue
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-black sm:text-2xl text-xl p-5 m-0 border-b">
                Enter your verification code
              </h1>
              <div className="flex flex-col px-5 pt-5">
                <h3 className="font-black mb-4">
                  To finish setup, please enter the 6-digit verification code generated by your
                  authenticator app.
                </h3>
                <div className="flex space-x-2 justify-center pb-4">
                  {inputs.map((input, index) => (
                    <input
                      key={index}
                      id={`setup-otp-input-${index}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={input}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      autoComplete="off"
                      spellCheck="false"
                      disabled={isVerifying}
                      className="w-10 h-10 md:w-12 md:h-12 border border-gray-300 rounded-lg text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  ))}
                </div>
              </div>
              <div className="p-4 border-t flex items-center justify-end gap-4">
                <button
                  className="btn btn-primary m-0 h-10 font-bold tracking-widest px-3 py-1 w-auto"
                  onClick={handleBackToQr}>
                  Back
                </button>
                <button
                  className="btn btn-primary m-0 h-10 font-bold tracking-widest px-3 py-1 w-auto"
                  onClick={verifyCode}
                  disabled={isVerifying}>
                  {isVerifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MFAScannerModal;